import { ApiTranslation } from '../api-translation';
import { CurrencyView } from '../currency-view';
import { SpanLoading } from '../fetch-status/span-loading';
import { TextTooltip } from '../text-tooltip';
import { UploadedImage } from '../uploaded-image';
import { InteractRefreshIcon, ViewCheckmarkIcon, ViewChevronDownIcon } from '@/client/assets';
import { useAppSettingStore } from '@/client/store/app-store';
import { OptionSide } from '@/client/store/trade-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { getDefaultImg } from '@/client/utils/markdown';
import { useUserPositions } from '@/libs/react-sdk/api/use-user-options';
import { MarketDetail } from '@/libs/sdk/api';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type TreadOptionsProps = {
  className?: string;
  type: 'buy' | 'sell';
  market: MarketDetail;
  optionAddress?: string;
  setOptionAddress?: (optionAddress: string) => void;
  optionSide?: OptionSide;
  setOptionSide?: (optionSide?: OptionSide) => void;
  uid: string;
};

export const TreadOptions = ({ type, market, optionAddress, uid, setOptionAddress, optionSide, setOptionSide }: TreadOptionsProps) => {
  const { t } = useTranslation();
  // Binary yes/no sides. Match by name first, then fall back to index position
  // so custom-named binaries (e.g. Up/Down, team names) still render their two
  // trade buttons instead of vanishing. Mirrors getBinaryMarketOptions.
  const { yes, no } = useMemo(() => {
    let y = market.optionList.find(item => {
      const n = item.optionName.trim().toLowerCase();
      return n === 'yes' || n === 'option1';
    });
    let n = market.optionList.find(item => {
      const nm = item.optionName.trim().toLowerCase();
      return nm === 'no' || nm === 'option2';
    });
    if (!y && !n && market.optionList.length === 2) {
      y = market.optionList[0];
      n = market.optionList[1];
    }
    return { yes: y, no: n };
  }, [market.optionList]);
  const baseTokenAddress = useAppSettingStore(state => state.baseToken.address);
  
  // Check if this is a multi-option market (more than 2 options)
  const isMultiOption = market.optionList.length > 2;
  
  const params = useMemo(() => {
    return {
      page: 1,
      pageSize: market.optionList.length,
      // Positions are collateral-filtered by the backend; scope to THIS market's
      // own collateral (not the global lens) so holdings/sell work when the header
      // lens differs from the viewed market. Fallback to lens for legacy markets.
      baseTokenAddress: market.baseTokenAddress || baseTokenAddress,
      marketAddress: market.marketAddress,
      uid,
    };
  }, [baseTokenAddress, market.baseTokenAddress, market.marketAddress, market.optionList.length, uid]);
  const userPositions = useUserPositions(api, params);
  const positions = useMemo(
    () => userPositions.data?.pages.flatMap(item => item.positions) || [],
    [userPositions.data?.pages]
  );
  const allOptionsWithBalance = useMemo(() => {
    return market.optionList.map(option => {
      const balance =
        positions.find(({ optionAddress }) => optionAddress.toLowerCase() === option.optionAddress.toLowerCase())?.balance ??
        0;
      const optionPicUrl = getDefaultImg(option.description) || option.optionPicUrl;
      return { ...option, balance, optionPicUrl };
    });
  }, [market.optionList, positions]);
  const optionList = useMemo(() => {
    if (type == 'sell' && isMultiOption) {
      return allOptionsWithBalance.filter(item => Number(item.balance) > 0);
    }
    return allOptionsWithBalance;
  }, [allOptionsWithBalance, type, isMultiOption]);
  const currentOption = useMemo(
    () => optionList.find(item => item.optionAddress.toLowerCase() === optionAddress?.toLowerCase()),
    [optionList, optionAddress]
  );

  useEffect(() => {
    // Auto-select first option for multi-option markets (both buy and sell)
    if (isMultiOption && optionAddress == undefined && optionList.length > 0) {
      setOptionAddress?.(optionList[0].optionAddress);
    }
  }, [optionAddress, optionList, setOptionAddress, type, isMultiOption]);

  useEffect(() => {
    // Auto-select YES when a multi-option is selected
    if (type === 'buy' && isMultiOption && optionAddress && optionSide === undefined) {
      setOptionSide?.('yes');
    }
  }, [type, isMultiOption, optionAddress, optionSide, setOptionSide]);

  useEffect(() => {
    // Auto-select for binary sell: pick the option with balance > 0
    if (type === 'sell' && !isMultiOption && optionAddress == undefined && yes && no) {
      const yesOpt = allOptionsWithBalance.find(o => o.optionAddress.toLowerCase() === yes.optionAddress.toLowerCase());
      const noOpt = allOptionsWithBalance.find(o => o.optionAddress.toLowerCase() === no.optionAddress.toLowerCase());
      if (Number(yesOpt?.balance) > 0) setOptionAddress?.(yes.optionAddress);
      else if (Number(noOpt?.balance) > 0) setOptionAddress?.(no.optionAddress);
    }
  }, [type, isMultiOption, optionAddress, yes, no, allOptionsWithBalance, setOptionAddress]);
  return (
    <div className="w-full space-y-2">
      <div className="flex items-center justify-between text-foreground/70">
        <div className="text-sm leading-5">{type == 'buy' ? t('outcome') : t('position')}</div>
        <button
          className="size-4 overflow-hidden"
          disabled={userPositions.isFetching}
          onClick={() => {
            userPositions.refetch();
          }}
        >
          <InteractRefreshIcon
            className={cn(
              'size-4 duration-200 fill-foreground/70 hover:rotate-90',
              userPositions.isFetching && 'animate-spin '
            )}
          />
        </button>
      </div>
      {/* option select - for multi-option markets (both buy and sell) */}
      {isMultiOption && (
        <div className="pos-relative">
          <Listbox value={optionAddress} onChange={setOptionAddress}>
            <ListboxButton
              disabled={optionList.length == 0}
              className={cn(
                'flex min-h-14 py-2 px-4 text-xs pr-4 items-center w-full border border-foreground/10 hover:bg-foreground/10 rounded-3 disabled:bg-foreground/5 disabled:text-foreground/10',
                optionList.length == 0 ? 'justify-center' : 'justify-between',
                'data-[active]:bg-foreground/10'
              )}
            >
              {userPositions.isLoading && <SpanLoading />}
              {optionList.length != 0 && !currentOption && t('select-a-outcome')}
              {type == 'sell' && optionList.length == 0 && t('no-available-positions')}
              {currentOption && (
                <div className="flex gap-3 items-center">
                  <UploadedImage fileKey={currentOption.optionPicUrl} className="size-10 rounded-1" />
                  <div className="space-y-1">
                    <TextTooltip
                      tooltip={currentOption.description}
                      asChild
                      className="line-clamp-1 text-sm text-start flex-1"
                    >
                      <ApiTranslation>{currentOption.description}</ApiTranslation>
                    </TextTooltip>
                    {type === 'buy' && isMultiOption && currentOption && (
                      <div className="text-xs inline-flex items-center gap-1 text-start text-foreground/70">
                        <CurrencyView
                          isLoading={userPositions.isLoading}
                          value={currentOption?.price}
                          decimal={currentOption.decimal}
                          precision={2}
                        />
                        <span>{t('chance', 'chance')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {optionList.length != 0 && <ViewChevronDownIcon className="size-4 fill-current" />}
            </ListboxButton>
            <ListboxOptions
              modal={false}
              className="min-w-[300px] pos-absolute w-full shadow-xl shadow-foreground/20 mt-2 z-50 bg-card p-2 rounded-3 border border-foreground/10 divide-y-1 divide-foreground/10 cursor-pointer"
            >
              {optionList.map(option => (
                <ListboxOption
                  key={`option-${option.optionAddress}`}
                  value={option.optionAddress}
                  className={cn(
                    'flex gap-3 text-sm p-2 items-center hover:bg-foreground/5 px-2 rounded-3',
                    optionAddress === option.optionAddress && 'bg-foreground/5'
                  )}
                >
                  <UploadedImage fileKey={option.optionPicUrl} className="size-10 rounded-1" />
                  <div className="space-y-1 flex-1">
                    <TextTooltip tooltip={option.description} className="line-clamp-1 text-start flex-1">
                      <ApiTranslation>{option.description}</ApiTranslation>
                    </TextTooltip>
                    {type === 'sell' && (
                      <span className="text-xs inline-flex items-center gap-1">
                        <CurrencyView isLoading={userPositions.isLoading} value={option?.balance} decimal={option.decimal} />
                        <span>{t('shares')}</span>
                      </span>
                    )}
                    {type === 'buy' && isMultiOption && (
                      <span className="text-xs inline-flex items-center gap-1 text-foreground/70">
                        <CurrencyView isLoading={userPositions.isLoading} value={option?.price} decimal={option.decimal} precision={2} />
                        <span>{t('chance', 'chance')}</span>
                      </span>
                    )}
                  </div>
                  {optionAddress === option.optionAddress && <ViewCheckmarkIcon className="size-5" />}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Listbox>
        </div>
      )}
      {/* Sell YES/NO buttons for multi-option market */}
      {type == 'sell' && isMultiOption && currentOption && (
        <div className="flex items-end justify-between flex-1 gap-3 pt-2">
          <button
            type="button"
            className={cn(
              'px-4 py-3 flex-1 rounded-2 min-h-12 font-semibold leading-none',
              'bg-beneficial text-white'
            )}
            onClick={() => setOptionAddress?.(currentOption.optionAddress)}
          >
            <div>YES</div>
            <div className="text-xs">
              <CurrencyView value={currentOption.balance} decimal={currentOption.decimal} /> {t('shares')}
            </div>
          </button>
          <button
            type="button"
            disabled
            className={cn(
              'px-4 py-3 flex-1 rounded-2 min-h-12 font-semibold leading-none',
              'bg-destructive/20 text-destructive-foreground opacity-50'
            )}
          >
            <div>NO</div>
            <div className="text-xs">0 {t('shares')}</div>
          </button>
        </div>
      )}
      {/* Buy YES/NO buttons for multi-option market */}
      {type == 'buy' && isMultiOption && currentOption && (
        <div className="flex items-end justify-between flex-1 gap-3 pt-2">
          <button
            type="button"
            className={cn(
              'px-4 py-3 flex-1 rounded-2 min-h-12 font-semibold leading-none inline-flex items-center justify-center gap-1',
              'hover:bg-beneficial hover:text-white bg-beneficial/20 text-beneficial-foreground',
              optionSide === 'yes' && 'bg-beneficial text-white'
            )}
            onClick={() => {
              setOptionSide?.('yes');
            }}
          >
            <span>YES</span>
            <span>
              {Math.round((Number(currentOption.price) / Math.pow(10, currentOption.decimal)) * 100)}¢
            </span>
          </button>
          <button
            type="button"
            className={cn(
              'px-4 py-3 flex-1 rounded-2 min-h-12 font-semibold leading-none inline-flex items-center justify-center gap-1',
              'hover:bg-destructive hover:text-white bg-destructive/20 text-destructive-foreground',
              optionSide === 'no' && 'bg-destructive text-white'
            )}
            onClick={() => {
              setOptionSide?.('no');
            }}
          >
            <span>NO</span>
            <span>
              {Math.round(((Math.pow(10, currentOption.decimal) - Number(currentOption.price)) / Math.pow(10, currentOption.decimal)) * 100)}¢
            </span>
          </button>
        </div>
      )}
      {/* select yes no - for binary markets only */}
      {type == 'buy' && !isMultiOption && yes && no && (
        <div className="flex items-end justify-between flex-1 gap-3">
          <button
            type="button"
            className={cn(
              'px-4 py-3 flex-1 rounded-2 min-h-12 font-semibold leading-none inline-flex items-center justify-center gap-1',
              'hover:bg-beneficial hover:text-white bg-beneficial/20 text-beneficial-foreground',
              optionAddress === yes.optionAddress && 'bg-beneficial text-white'
            )}
            onClick={() => {
              setOptionAddress?.(yes.optionAddress);
            }}
          >
            <ApiTranslation>{yes.description}</ApiTranslation>
            <CurrencyView isLoading={userPositions.isLoading} value={yes.price} decimal={yes.decimal} />
          </button>
          <button
            type="button"
            className={cn(
              'px-4 py-3 flex-1 rounded-2 min-h-12 font-semibold leading-none inline-flex items-center justify-center gap-1',
              'hover:bg-destructive hover:text-white bg-destructive/20 text-destructive-foreground',
              optionAddress === no.optionAddress && 'bg-destructive text-white'
            )}
            onClick={() => {
              setOptionAddress?.(no.optionAddress);
            }}
          >
            <ApiTranslation>{no.description}</ApiTranslation>
            <CurrencyView isLoading={userPositions.isLoading} value={no.price} decimal={no.decimal} />
          </button>
        </div>
      )}
      {/* Sell YES/NO buttons for binary markets */}
      {type == 'sell' && !isMultiOption && yes && no && (
        <div className="flex items-end justify-between flex-1 gap-3">
          {(() => {
            const yesOpt = allOptionsWithBalance.find(o => o.optionAddress.toLowerCase() === yes.optionAddress.toLowerCase());
            const noOpt = allOptionsWithBalance.find(o => o.optionAddress.toLowerCase() === no.optionAddress.toLowerCase());
            const yesBalance = Number(yesOpt?.balance ?? 0);
            const noBalance = Number(noOpt?.balance ?? 0);
            return (
              <>
                <button
                  type="button"
                  disabled={yesBalance === 0}
                  className={cn(
                    'px-4 py-3 flex-1 rounded-2 min-h-12 font-semibold leading-none',
                    'hover:bg-beneficial hover:text-white bg-beneficial/20 text-beneficial-foreground',
                    optionAddress === yes.optionAddress && 'bg-beneficial text-white',
                    yesBalance === 0 && 'opacity-50'
                  )}
                  onClick={() => setOptionAddress?.(yes.optionAddress)}
                >
                  <div><ApiTranslation>{yes.description || 'YES'}</ApiTranslation></div>
                  <div className="text-xs">
                    {yesOpt && <CurrencyView value={yesOpt.balance} decimal={yesOpt.decimal} />} {t('shares')}
                  </div>
                </button>
                <button
                  type="button"
                  disabled={noBalance === 0}
                  className={cn(
                    'px-4 py-3 flex-1 rounded-2 min-h-12 font-semibold leading-none',
                    'hover:bg-destructive hover:text-white bg-destructive/20 text-destructive-foreground',
                    optionAddress === no.optionAddress && 'bg-destructive text-white',
                    noBalance === 0 && 'opacity-50'
                  )}
                  onClick={() => setOptionAddress?.(no.optionAddress)}
                >
                  <div><ApiTranslation>{no.description || 'NO'}</ApiTranslation></div>
                  <div className="text-xs">
                    {noOpt && <CurrencyView value={noOpt.balance} decimal={noOpt.decimal} />} {t('shares')}
                  </div>
                </button>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};
